import React, { createContext, useState, useEffect, useContext, useRef } from "react";
import {InferenceSession} from "onnxruntime-web";
import {modelData, onnxMaskToImage, calcImageScale, arrayToImageData, samPointsAreSimilar} from "../js/SAMUtils";
import npyjs from "npyjs";
import {debounceAsync} from "../js/utils";

const ort = require("onnxruntime-web");


export const ONNXStateContext = createContext(null);
export const ONNXActionsContext = createContext(null);

const ONNXProvider = props => {
    const workerRef = useRef(null);
    const [modelLoaded, setModelLoaded] = useState(false);
    const [tensor, setTensor] = useState(null); // Image embedding tensor
    const [maskImg, setMaskImg] = useState(null); // Output binary mask
    const [lastProcessedPoints, setLastProcessedPoints] = useState(null);
    const [maskImageData, setMaskImageData] = useState(null);
    const [ready, setReady] = useState(false);

    const modelLoadedRef = useRef(modelLoaded);
    const tensorRef = useRef(tensor);
    const lastProcessedPointsRef = useRef(lastProcessedPoints);
    const readyRef = useRef(ready);

    useEffect(() => { modelLoadedRef.current = modelLoaded; }, [modelLoaded]);
    useEffect(() => { tensorRef.current = tensor; }, [tensor]);
    useEffect(() => { lastProcessedPointsRef.current = lastProcessedPoints; }, [lastProcessedPoints]);
    useEffect(() => { readyRef.current = ready; }, [ready]);

    // The ONNX model expects the input to be rescaled to 1024.
    // The modelScale state variable keeps track of the scale values.
    const [modelScale, setModelScale] = useState(null);
    const modelScaleRef = useRef(modelScale);
    useEffect(() => { modelScaleRef.current = modelScale; }, [modelScale]);

    const resetSAMState = React.useCallback(() => {
        setMaskImg(null);
        setLastProcessedPoints(null);
        setMaskImageData(null);
        setTensor(null);
    }, []);

    useEffect(() => {
        // Initialize the Web Worker
        const worker = new Worker(new URL("../workers/sam.worker.js", import.meta.url));
        workerRef.current = worker;

        worker.onmessage = (e) => {
            const { type, data, error } = e.data;
            switch (type) {
                case "modelLoaded":
                    setModelLoaded(true);
                    setReady(true);
                    break;
                case "inferenceResults":
                    const { outputData, outputDims } = data;
                    const imageData = arrayToImageData(outputData, outputDims[2], outputDims[3]);
                    const image = onnxMaskToImage(outputData, outputDims[2], outputDims[3]);
                    setMaskImageData(imageData);
                    setMaskImg(image);
                    setReady(true);
                    break;
                case "error":
                    console.error("Worker error:", error);
                    setReady(true);
                    break;
            }
        };

        const modelUrl = window.location.origin + "/model/facebook_decoder_quant.onnx";
        worker.postMessage({
            type: "loadModel",
            data: { modelUrl }
        });

        return () => {
            worker.terminate();
        };
    }, []);

    // Decode a Numpy file into a tensor.
    const loadNpyTensor = React.useCallback(async (tensorFile, dType) => {
        let npLoader = new npyjs();
        const npArray = await npLoader.load(tensorFile);
        const tensor = new ort.Tensor(dType, npArray.data, npArray.shape);
        return tensor;
    }, []);

    const loadEmbeddingsFromFile = React.useCallback((filename) => {
        Promise.resolve(loadNpyTensor(filename, "float32")).then(
            (embedding) => {
                setTensor(embedding);
                console.log("Loaded embeddings from file")
            }
        );
    }, [loadNpyTensor]);

    const parseImage = React.useCallback((url) => {
        try {
            const img = new Image();
            img.src = url;
            img.onload = () => {
                const { height, width, samScale } = calcImageScale(img);
                setModelScale({
                    height: height,  // original image height
                    width: width,  // original image width
                    samScale: samScale, // scaling factor for image which has been resized to longest side 1024
                });
            };
        } catch (error) {
            console.log(error);
        }
    }, []);

    const runONNX = React.useMemo(() => debounceAsync(async (points) => {
        if (
            !workerRef.current ||
            !modelLoadedRef.current ||
            tensorRef.current === null ||
            modelScaleRef.current === null ||
            points.length === 0 ||
            !readyRef.current
        )
            return;

        if (lastProcessedPointsRef.current && lastProcessedPointsRef.current.length > 0)
            if (samPointsAreSimilar(lastProcessedPointsRef.current, points))
                return;

        try {
            // Prepare the model input in the correct format for SAM.
            // The modelData function is from onnxModelAPI.tsx.
            const feeds = modelData({
                points,
                tensor: tensorRef.current,
                modelScale: modelScaleRef.current
            });
            if (feeds === undefined) return;
            setLastProcessedPoints(points);
            setReady(false);
            
            // Run the SAM ONNX model in the worker
            workerRef.current.postMessage({
                type: "runInference",
                data: { feeds }
            });
        } catch (e) {
            console.log(e);
            setReady(true);
        }
    }, 250), []);

    const stateValue = React.useMemo(() => ({
        modelLoaded, tensor, maskImg, lastProcessedPoints, ready, maskImageData
    }), [modelLoaded, tensor, maskImg, lastProcessedPoints, ready, maskImageData]);

    const actionsValue = React.useMemo(() => ({
        setTensor, loadNpyTensor, loadEmbeddingsFromFile, runONNX, setMaskImg,
        parseImage, setLastProcessedPoints, resetSAMState
    }), [loadNpyTensor, loadEmbeddingsFromFile, runONNX, parseImage, resetSAMState]);

    return (
        <ONNXStateContext.Provider value={stateValue}>
            <ONNXActionsContext.Provider value={actionsValue}>
                {props.children}
            </ONNXActionsContext.Provider>
        </ONNXStateContext.Provider>
    );
}

export const useONNXState = () => useContext(ONNXStateContext);
export const useONNXActions = () => useContext(ONNXActionsContext);

// Keep useONNXContext for backward compatibility
export const useONNXContext = () => {
    const state = useContext(ONNXStateContext);
    const actions = useContext(ONNXActionsContext);
    return React.useMemo(() => ({ ...state, ...actions }), [state, actions]);
};

export default ONNXProvider;
